// DV-5184 — deterministic, fully typed fixtures for the group-events API.
// Shared by specs and by group-events-mock.interceptor.ts (dev-only, environment.mockGroupEvents).
// Ids are stable on purpose: specs and the legacy-response decoration cycle through them.
import {
    AttendanceType,
    CreateGroupEventParam,
    DayOfWeek,
    DuplicateGroupEventResponse,
    EligiblePackageResponse,
    GroupEventDetailResponse,
    GroupEventInsightsResponse,
    GroupEventListItem,
    GroupEventPoliciesResponse,
    GroupEventType,
    GroupEventTypeCount,
    OnlineBookingRule,
    PackageRequirement,
    PayRateOptionResponse,
    RepeatType,
    ScheduleListItemResponse,
    SessionStatus,
    SessionsSummaryResponse
} from '@@interfaces/group-event.interface'

// Local assets so mocks never depend on the network.
const avatar = (n: number): string => `/images/avatars/thumb-${n}.jpg`
const CLASS_PICTURE = '/images/defaultclass.png'

// Id handed back for anything the mock "creates" (POST with-setup / duplicate).
export const MOCK_CREATED_GROUP_EVENT_ID = 999

export const GROUP_EVENT_TYPE_COUNTS: GroupEventTypeCount[] = [
    { type: GroupEventType.CLASS, label: 'Class', count: 3 },
    { type: GroupEventType.WORKSHOP, label: 'Workshop', count: 2 },
    { type: GroupEventType.TEAM, label: 'Team', count: 1 }
]

// 6 classes: CLASS ×3, WORKSHOP ×2, TEAM ×1 — both attendance types and every capacityDisplay variant.
export const GROUP_EVENTS: GroupEventListItem[] = [
    {
        id: 101,
        title: 'Morning Yoga',
        description:
            'A gentle flow to start the day. Suitable for all levels; mats provided.',
        pictureUrl: CLASS_PICTURE,
        type: GroupEventType.CLASS,
        categoryName: 'Yoga',
        levelName: 'Open Level',
        activityLevelsNames: [],
        fromAge: 16,
        toAge: null,
        price: 25,
        attendanceType: AttendanceType.OPEN,
        activeScheduleCount: 3,
        instructorAvatars: [avatar(1), avatar(2)],
        capacityDisplay: {
            type: AttendanceType.OPEN,
            enrolled: null,
            totalCapacity: null,
            maxCapacity: 20
        },
        instructors: [
            { id: 11, name: 'Tania Roberts', avatarUrl: avatar(1) },
            { id: 12, name: 'John Carter', avatarUrl: avatar(2) }
        ],
        legacyScheduleGroupId: 5001,
        archivedScheduleCount: 2,
        idCategory: 11,
        idSkillLevels: [],
        idActivityLevels: [],
        idBranch: 42
    },
    {
        id: 102,
        title: 'Kids Karate',
        description:
            'Structured belt programme for children. Enrollment required.',
        pictureUrl: CLASS_PICTURE,
        type: GroupEventType.CLASS,
        categoryName: 'Martial Arts',
        levelName: 'Beginner',
        activityLevelsNames: ['Beginner'],
        fromAge: 6,
        toAge: 12,
        attendanceType: AttendanceType.ENROLLMENT,
        activeScheduleCount: 5,
        instructorAvatars: [avatar(1), avatar(2), avatar(3), avatar(4)],
        capacityDisplay: {
            type: AttendanceType.ENROLLMENT,
            enrolled: 25,
            totalCapacity: 75,
            maxCapacity: null
        },
        // 6 names → "Tania, John +4" in the avatar stack.
        instructors: [
            { id: 11, name: 'Tania Roberts', avatarUrl: avatar(1) },
            { id: 12, name: 'John Carter', avatarUrl: avatar(2) },
            { id: 13, name: 'Maya Haddad', avatarUrl: avatar(3) },
            { id: 14, name: 'Omar Khalil', avatarUrl: avatar(4) },
            { id: 15, name: 'Lea Nassar', avatarUrl: null },
            { id: 16, name: 'Sam Okafor', avatarUrl: null }
        ],
        legacyScheduleGroupId: 5002,
        archivedScheduleCount: 0,
        idCategory: 12,
        idSkillLevels: [1],
        idActivityLevels: [],
        idBranch: 42
    },
    {
        id: 103,
        title: 'HIIT Bootcamp',
        description: 'High-intensity interval training. Bring water.',
        type: GroupEventType.CLASS,
        categoryName: 'Fitness',
        levelName: null,
        activityLevelsNames: ['Intermediate', 'Advanced'],
        fromAge: 18,
        toAge: 60,
        price: 15,
        attendanceType: AttendanceType.OPEN,
        activeScheduleCount: 2,
        instructorAvatars: [avatar(5)],
        capacityDisplay: {
            type: AttendanceType.OPEN,
            enrolled: null,
            totalCapacity: null,
            maxCapacity: 15
        },
        instructors: [{ id: 17, name: 'Chris Doyle', avatarUrl: avatar(5) }],
        legacyScheduleGroupId: 5003,
        archivedScheduleCount: 1,
        idCategory: 13,
        idSkillLevels: [],
        idActivityLevels: [2, 3],
        idBranch: 42
    },
    {
        id: 201,
        title: 'Pottery Basics',
        description: 'Four-week wheel-throwing course for beginners.',
        pictureUrl: CLASS_PICTURE,
        type: GroupEventType.WORKSHOP,
        categoryName: 'Arts & Crafts',
        levelName: 'Beginner',
        activityLevelsNames: ['Beginner'],
        fromAge: null,
        toAge: 14,
        attendanceType: AttendanceType.ENROLLMENT,
        activeScheduleCount: 1,
        instructorAvatars: [avatar(6)],
        capacityDisplay: {
            type: AttendanceType.ENROLLMENT,
            enrolled: 10,
            totalCapacity: 12,
            maxCapacity: null
        },
        instructors: [{ id: 18, name: 'Rana Saleh', avatarUrl: avatar(6) }],
        legacyScheduleGroupId: null,
        archivedScheduleCount: 0,
        idCategory: 14,
        idSkillLevels: [1],
        idActivityLevels: [],
        idBranch: 42
    },
    {
        id: 202,
        title: 'Photography Weekend',
        description: 'Two-day outdoor photography intensive.',
        type: GroupEventType.WORKSHOP,
        categoryName: 'Arts & Crafts',
        levelName: null,
        activityLevelsNames: [],
        fromAge: null,
        toAge: null,
        price: 80,
        attendanceType: AttendanceType.OPEN,
        activeScheduleCount: 0,
        instructorAvatars: [],
        // Nullable-everything variant: renders as "—".
        capacityDisplay: {
            type: AttendanceType.OPEN,
            enrolled: null,
            totalCapacity: null,
            maxCapacity: null
        },
        instructors: [],
        legacyScheduleGroupId: null,
        archivedScheduleCount: 0,
        idCategory: 14,
        idSkillLevels: [],
        idActivityLevels: [],
        idBranch: 42
    },
    {
        id: 301,
        title: 'U14 Football Team',
        description: 'Competitive squad training, two evenings a week.',
        pictureUrl: CLASS_PICTURE,
        type: GroupEventType.TEAM,
        categoryName: 'Football',
        levelName: 'Advanced',
        activityLevelsNames: ['Advanced'],
        fromAge: 12,
        toAge: 14,
        attendanceType: AttendanceType.ENROLLMENT,
        activeScheduleCount: 2,
        instructorAvatars: [avatar(7), avatar(8)],
        // Full roster: 100 %.
        capacityDisplay: {
            type: AttendanceType.ENROLLMENT,
            enrolled: 18,
            totalCapacity: 18,
            maxCapacity: null
        },
        instructors: [
            { id: 19, name: 'Karim Aoun', avatarUrl: avatar(7) },
            { id: 20, name: 'Dana Farah', avatarUrl: avatar(8) }
        ],
        legacyScheduleGroupId: 5301,
        archivedScheduleCount: 3,
        idCategory: 15,
        idSkillLevels: [3],
        idActivityLevels: [],
        idBranch: 42
    }
]

export const GROUP_EVENT_IDS: number[] = GROUP_EVENTS.map((g) => g.id)

// Unknown ids still resolve (first fixture re-keyed) so deep links never dead-end in mock mode.
export const findGroupEvent = (id: number): GroupEventDetailResponse => {
    const hit = GROUP_EVENTS.find((g) => g.id === id)
    return hit ? hit : { ...GROUP_EVENTS[0], id }
}

// Detail shape the API echoes after POST /group-events or /with-setup.
export const buildCreatedGroupEvent = (
    param: CreateGroupEventParam,
    id: number = MOCK_CREATED_GROUP_EVENT_ID
): GroupEventDetailResponse => ({
    id,
    title: param.title,
    description: param.description,
    pictureUrl: param.pictureUrl,
    type: param.type,
    categoryName: undefined,
    levelName: null,
    activityLevelsNames: [],
    fromAge: param.fromAge ?? null,
    toAge: param.toAge ?? null,
    price: param.price,
    attendanceType: param.attendanceType,
    activeScheduleCount: 0,
    instructorAvatars: [],
    capacityDisplay: {
        type: param.attendanceType,
        enrolled: param.attendanceType === AttendanceType.ENROLLMENT ? 0 : null,
        totalCapacity: null,
        maxCapacity: null
    },
    instructors: [],
    legacyScheduleGroupId: null,
    archivedScheduleCount: 0,
    idCategory: param.idCategory ?? null,
    idSkillLevels: param.idSkillLevels ?? [],
    idActivityLevels: param.idActivityLevels ?? [],
    idBranch: param.idBranch
})

export const DUPLICATE_GROUP_EVENT_RESPONSE: DuplicateGroupEventResponse = {
    groupEventId: MOCK_CREATED_GROUP_EVENT_ID
}

// 3 schedules: weekly with a cancelled session, biweekly ongoing, one-time archived.
export const SCHEDULES: ScheduleListItemResponse[] = [
    {
        id: 9001,
        recurrenceLabel: 'Wednesdays at 03:00 PM',
        duration: 75,
        startsOn: '2026-01-07',
        endsOn: '2026-12-30',
        instructorName: 'Tania Roberts',
        payRateName: 'Standard Class Rate',
        subTeacherNames: ['John Carter'],
        roomName: 'Room 3',
        capacity: 20,
        repeatType: RepeatType.WEEKLY,
        isArchived: false,
        mandatory: true,
        sessions: [
            {
                id: 7003,
                groupEventId: 101,
                groupEventTitle: 'Morning Yoga',
                scheduleId: 9001,
                startDate: '2026-04-29',
                startTime: '15:00',
                endTime: '16:15',
                isCancelled: true,
                trainers: [{ id: 11, name: 'Tania Roberts' }],
                location: 'Room 3',
                branch: 'Downtown',
                capacity: 20
            }
        ],
        timeSlots: [
            { dayOfWeek: DayOfWeek.WEDNESDAY, startTime: '15:00', duration: 75 }
        ],
        idTrainer: 11,
        idPayRate: 3,
        subTeacherIds: [12],
        idLocation: 7
    },
    {
        id: 9002,
        recurrenceLabel: 'Saturdays at 09:00 AM',
        duration: 60,
        startsOn: '2026-03-07',
        endsOn: null,
        instructorName: 'John Carter',
        payRateName: null,
        subTeacherNames: [],
        roomName: null,
        capacity: null,
        repeatType: RepeatType.BIWEEKLY,
        isArchived: false,
        mandatory: false,
        sessions: [],
        timeSlots: [
            { dayOfWeek: DayOfWeek.SATURDAY, startTime: '09:00', duration: 60 }
        ],
        idTrainer: 12,
        idPayRate: null,
        subTeacherIds: [],
        idLocation: null
    },
    {
        id: 9003,
        recurrenceLabel: 'Friday, Feb 13, 2026 at 06:00 PM',
        duration: 90,
        startsOn: '2026-02-13',
        endsOn: '2026-02-13',
        instructorName: 'Tania Roberts',
        payRateName: 'Standard Class Rate',
        subTeacherNames: [],
        roomName: 'Studio A',
        capacity: 12,
        repeatType: RepeatType.ONE_TIME,
        isArchived: true,
        sessions: [],
        timeSlots: [
            { dayOfWeek: DayOfWeek.FRIDAY, startTime: '18:00', duration: 90 }
        ],
        idTrainer: 11,
        idPayRate: 3,
        subTeacherIds: [],
        idLocation: 8
    }
]

export const ELIGIBLE_PACKAGES: EligiblePackageResponse[] = [
    {
        membershipId: 501,
        requirement: PackageRequirement.INCLUDED,
        name: 'Unlimited Monthly',
        membershipType: 'UNLIMITED'
    },
    {
        membershipId: 502,
        requirement: PackageRequirement.USES_PASS,
        quantity: 2,
        name: '10 Class Pass',
        membershipType: 'PASSES'
    },
    {
        membershipId: 503,
        requirement: PackageRequirement.PRICE_PER_CLASS,
        price: 20,
        name: 'Drop-in',
        membershipType: 'DROP_IN'
    }
]

// Two overridden fields so both badges render.
export const GROUP_EVENT_POLICIES: GroupEventPoliciesResponse = {
    onlinePaymentRule: {
        value: OnlineBookingRule.OPTIONAL,
        isOverridden: false,
        tenantDefault: OnlineBookingRule.OPTIONAL
    },
    cancellationWindowMinutes: {
        value: 1440,
        isOverridden: true,
        tenantDefault: 720
    },
    cancellationFee: { value: 10, isOverridden: true, tenantDefault: 0 },
    waitlistEnabled: { value: true, isOverridden: false, tenantDefault: true },
    maximumWaitlistClients: {
        value: 5,
        isOverridden: false,
        tenantDefault: 5
    },
    waiverEnabled: { value: false, isOverridden: false, tenantDefault: false }
}

const SESSION_IDS = [7001, 7002, 7003, 7004, 7005, 7006, 7007, 7008]

// Column order = SESSION_IDS order; every SessionStatus appears at least once.
const statusRow = (statuses: SessionStatus[]) =>
    statuses.map((status, i) => ({ sessionId: SESSION_IDS[i], status }))

export const GROUP_EVENT_INSIGHTS: GroupEventInsightsResponse = {
    uniqueAttendees: 50,
    attendanceRatePercent: 76,
    capacityUtilizationPercent: 83,
    estimatedRevenue: 1200,
    waitlistMissedRevenue: 150,
    waitlistMissedCount: 3,
    registeredStudents: 41,
    totalCapacity: 50,
    attendancePerStudent: 6.4,
    missedClasses: 12,
    retentionPercent: 88,
    // 8 consecutive Wednesdays.
    sessionHeaders: [
        {
            sessionId: 7001,
            date: '2026-04-15',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 18,
            bookedCount: 19
        },
        {
            sessionId: 7002,
            date: '2026-04-22',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 17,
            bookedCount: 18
        },
        {
            sessionId: 7003,
            date: '2026-04-29',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 0,
            bookedCount: 0
        },
        {
            sessionId: 7004,
            date: '2026-05-06',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 19,
            bookedCount: 20
        },
        {
            sessionId: 7005,
            date: '2026-05-13',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 14,
            bookedCount: 16
        },
        {
            sessionId: 7006,
            date: '2026-05-20',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 20,
            bookedCount: 20
        },
        {
            sessionId: 7007,
            date: '2026-05-27',
            dayOfWeek: 'WEDNESDAY',
            capacity: 20,
            attendedCount: 12,
            bookedCount: 15
        },
        {
            sessionId: 7008,
            date: '2026-06-03',
            dayOfWeek: 'WEDNESDAY',
            capacity: null,
            attendedCount: 0,
            bookedCount: 17
        }
    ],
    attendeeRows: [
        {
            clientId: 3001,
            bookingClientId: 4001,
            clientName: 'Emma Wilson',
            balance: 0,
            contactEmail: 'emma.wilson@example.com',
            contactPhone: '+1 555 010 0001',
            packageName: '10 Class Pass',
            packageExpiry: '2026-09-30',
            membershipStartDate: '2026-01-10',
            sessionStatuses: statusRow([
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.CANCELLED,
                SessionStatus.ATTENDED,
                SessionStatus.LATE_CANCEL,
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.BOOKED
            ]),
            avatarUrl: avatar(9),
            packageRenews: true
        },
        {
            clientId: 3002,
            bookingClientId: 4002,
            clientName: 'Liam Chen',
            balance: -50,
            contactEmail: 'liam.chen@example.com',
            contactPhone: '+1 555 010 0002',
            packageName: null,
            packageExpiry: null,
            membershipStartDate: null,
            sessionStatuses: statusRow([
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.CANCELLED,
                SessionStatus.ATTENDED,
                SessionStatus.EMPTY,
                SessionStatus.ATTENDED,
                SessionStatus.BOOKED,
                SessionStatus.WAITLIST
            ]),
            avatarUrl: null,
            packageRenews: false
        },
        {
            clientId: 3003,
            bookingClientId: 4003,
            clientName: 'Sofia Martinez',
            balance: -180,
            contactEmail: 'sofia.martinez@example.com',
            contactPhone: '+1 555 010 0003',
            packageName: 'Unlimited Monthly',
            packageExpiry: '2026-12-31',
            membershipStartDate: '2025-11-01',
            sessionStatuses: statusRow([
                SessionStatus.LATE_CANCEL,
                SessionStatus.ATTENDED,
                SessionStatus.CANCELLED,
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.BOOKED
            ]),
            avatarUrl: avatar(10),
            packageRenews: false
        },
        {
            clientId: 3004,
            bookingClientId: 4004,
            clientName: 'Noah Patel',
            balance: 0,
            contactEmail: 'noah.patel@example.com',
            contactPhone: '+1 555 010 0004',
            packageName: 'Drop-in',
            packageExpiry: null,
            membershipStartDate: '2026-04-01',
            sessionStatuses: statusRow([
                SessionStatus.EMPTY,
                SessionStatus.EMPTY,
                SessionStatus.CANCELLED,
                SessionStatus.WAITLIST,
                SessionStatus.ATTENDED,
                SessionStatus.ATTENDED,
                SessionStatus.EMPTY,
                SessionStatus.BOOKED
            ]),
            avatarUrl: null,
            packageRenews: false
        }
    ]
}

export const SESSIONS_SUMMARY: SessionsSummaryResponse = {
    totalEvents: 34,
    totalAttendees: 312,
    allAttendees: 320,
    totalCapacity: 450,
    revenue: 8500
}

export const PAY_RATES: PayRateOptionResponse[] = [
    { id: 3, name: 'Standard Class Rate', type: 'PER_CLASS', amount: 35 },
    { id: 4, name: 'Hourly', type: 'PER_HOUR', amount: 28 },
    { id: 5, name: 'Per Attendee', type: 'PER_ATTENDEE', amount: 4 }
]
